import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { MapPin, UtensilsCrossed, Wallet, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/landing/Footer';
import Seo from '../components/Seo/Seo';
import { breadcrumbSchema, faqSchema } from '../components/Seo/schemas';
import { CITIES } from './cityContent';

/**
 * Programmatic city landing pages: /pgs-in/:slug
 * Targets "cheap PG in {city} short stay" searches and funnels into the
 * existing search flow with the city prefilled.
 */
const CityLanding: React.FC = () => {
  const { slug } = useParams();
  const c = CITIES.find(x => x.slug === slug);
  if (!c) return <Navigate to="/" replace />;

  const searchLink = `/guest-search-results?keyword=${encodeURIComponent(c.city)}`;

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <Seo
        title={c.title}
        description={c.description}
        path={`/pgs-in/${c.slug}`}
        jsonLd={[
          breadcrumbSchema([[`PGs in ${c.city}`, `/pgs-in/${c.slug}`]]),
          faqSchema(c.faqs),
        ]}
      />

      {/* Simple header consistent with other marketing pages */}
      <header className="bg-background/95 border-b border-border">
        <div className="container mx-auto px-4 flex items-center justify-between h-16">
          <Link to="/" className="text-2xl font-bold">
            <span className="text-primary">Social</span>
            <span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded-md ml-1">Bunkr</span>
          </Link>
          <nav className="hidden sm:flex items-center gap-6 text-sm">
            <Link to="/" className="text-foreground/80 hover:text-primary">Home</Link>
            <Link to="/about" className="text-foreground/80 hover:text-primary">About</Link>
            <Link to="/faq" className="text-foreground/80 hover:text-primary">FAQs</Link>
          </nav>
          <Link to={searchLink}>
            <Button className="bg-secondary text-secondary-foreground hover:opacity-90 font-semibold">
              Find a Bed
            </Button>
          </Link>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero */}
        <section className="bg-deep-emerald-green text-white">
          <div className="container mx-auto px-4 py-14 max-w-4xl">
            <nav aria-label="Breadcrumb" className="text-sm text-white/60 mb-4">
              <Link to="/" className="hover:text-white">Home</Link>
              <span className="mx-1">/</span>
              <span className="text-white/90">PGs in {c.city}</span>
            </nav>
            <h1 className="text-3xl sm:text-4xl font-extrabold mb-4">{c.h1}</h1>
            <p className="text-white/85 max-w-2xl leading-relaxed mb-6">{c.intro}</p>
            <Link to={searchLink}>
              <Button size="lg" className="bg-warm-yellow text-deep-emerald-green font-bold hover:opacity-90">
                Search Beds in {c.city}
              </Button>
            </Link>
          </div>
        </section>

        {/* Quick facts strip */}
        <section className="container mx-auto px-4 -mt-6 max-w-4xl">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 grid grid-cols-2 sm:grid-cols-4 gap-4 p-5 text-sm">
            <div className="flex items-center gap-2"><Wallet className="h-4 w-4 text-primary" /> Budget nightly rates</div>
            <div className="flex items-center gap-2"><UtensilsCrossed className="h-4 w-4 text-primary" /> 3 meals included</div>
            <div className="flex items-center gap-2"><MessageCircle className="h-4 w-4 text-primary" /> WhatsApp confirmation</div>
            <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> {c.areas.length}+ localities</div>
          </div>
        </section>

        {/* Body sections */}
        <section className="container mx-auto px-4 py-10 max-w-4xl space-y-5">
          {c.points.map(p => (
            <div key={p.heading} className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{p.heading}</h2>
              <p className="text-gray-600 text-sm leading-relaxed">{p.body}</p>
            </div>
          ))}

          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Popular areas in {c.city}</h2>
            <div className="flex flex-wrap gap-2">
              {c.areas.map(a => (
                <Link key={a} to={`/guest-search-results?keyword=${encodeURIComponent(a)}`}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm px-3 py-1.5 rounded-full">
                  {a}
                </Link>
              ))}
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 pt-4">Questions travellers ask</h2>
          {c.faqs.map(f => (
            <details key={f.q} className="bg-white rounded-xl border border-gray-100 p-4">
              <summary className="font-medium text-gray-800 cursor-pointer">{f.q}</summary>
              <p className="text-gray-600 text-sm mt-2">{f.a}</p>
            </details>
          ))}

          <div className="bg-deep-emerald-green rounded-2xl p-6 text-white flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="font-semibold">Beds in {c.city} from budget nightly rates, meals included.</p>
            <Link to={searchLink}>
              <Button className="bg-warm-yellow text-deep-emerald-green font-bold hover:opacity-90">Book Now</Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CityLanding;
